import { TranslationKeys } from "src/@types/i18next";
import DealTypeEnum from "@common/enums/DealTypeEnum";
import { i18n } from "next-i18next";
import { NameId } from "@common/types/NameId";
import { AftermarketSources } from "./warranty/types/AftermarketSource";
import {
  allConsumerFinancingSteps,
  consumerFinancingChecklistStep,
} from "./consumer/types/ConsumerFinancingStep";

export type PurchaseFlow = "aftermarket" | "financing";
export type PurchaseStep = {
  id: number;
  flow: PurchaseFlow;
  nameTranslation: TranslationKeys["common"] | TranslationKeys["purchase"];
  order: number;
  href: string;
  isPointOfNoReturn: boolean;
  introHref?: string;
  guideTitle?: TranslationKeys["common"] | TranslationKeys["purchase"];
  guideDescription?: TranslationKeys["common"] | TranslationKeys["purchase"];
};

// Aftermarket flow steps

export const aftermarketQuoteStep: PurchaseStep = {
  id: 10,
  flow: "aftermarket",
  nameTranslation: "common:quote",
  order: 0,
  href: "/warranty/quote",
  isPointOfNoReturn: false,
};

export const aftermarketDetailsStep: PurchaseStep = {
  id: 8,
  flow: "aftermarket",
  nameTranslation: "common:details",
  order: 1,
  href: "/warranty/details",
  isPointOfNoReturn: false,
  introHref: "/introduction",
  guideTitle: "common:enter_your_details",
  guideDescription:
    "purchase:provide_information_about_your_vehicle_warranty_registration",
};

export const aftermarketCoverageStep: PurchaseStep = {
  id: 7,
  flow: "aftermarket",
  nameTranslation: "purchase:confirm_coverage",
  order: 2,
  href: "/warranty/configure",
  isPointOfNoReturn: false,
  guideTitle: "purchase:select_and_confirm_coverage",
  guideDescription: "purchase:tailor_warranty_offerings_by_coverage_type",
};

export const aftermarketContractStep: PurchaseStep = {
  id: 9,
  flow: "aftermarket",
  nameTranslation: "purchase:contract",
  order: 3,
  href: "/warranty/contract",
  isPointOfNoReturn: false,
  guideTitle: "purchase:e_sign_documents",
  guideDescription: "purchase:finalize_coverage_details_by_esigning_contracts",
};

export const aftermarketPaymentStep: PurchaseStep = {
  id: 11,
  flow: "aftermarket",
  nameTranslation: "purchase:payment",
  order: 4,
  href: "/warranty/payment",
  isPointOfNoReturn: false,
  guideTitle: "purchase:make_a_payment",
  guideDescription: "purchase:make_payment_using_preferred_card",
};

export const aftermarketCompleteStep: PurchaseStep = {
  id: 12,
  flow: "aftermarket",
  nameTranslation: "common:thank_you",
  order: 5,
  href: "/warranty/complete",
  isPointOfNoReturn: true,
  guideTitle: "purchase:provide_safety_inspection",
  guideDescription: "purchase:upload_copy_of_safety_to_prevent_inspection",
};

const allPurchaseSteps = {
  aftermarket: [
    aftermarketDetailsStep,
    aftermarketCoverageStep,
    aftermarketContractStep,
    aftermarketPaymentStep,
    aftermarketCompleteStep,
  ].sort((a, b) => a.order - b.order),
  financing: allConsumerFinancingSteps.sort(
    (a, b) => a.order - b.order
  ) as PurchaseStep[],
};

export const getAllPurchaseStepsForFlow = (flow: PurchaseFlow) =>
  allPurchaseSteps[flow];

export const getPreviousStep = (
  flow: PurchaseFlow,
  currentStep: PurchaseStep
) => {
  const steps = getAllPurchaseStepsForFlow(flow);
  const index = steps.findIndex((s) => s.id === currentStep.id);

  if (index === 0 || index === -1) {
    return undefined;
  }

  return steps[index - 1];
};

export const getNextStep = (flow: PurchaseFlow, currentStep: PurchaseStep) => {
  const steps = getAllPurchaseStepsForFlow(flow);
  const index = steps.findIndex((s) => s.id === currentStep.id);
  if (index === steps.length - 1 || index === -1) {
    return undefined;
  }
  return steps[index + 1];
};

export const purchaseLastStep: Record<PurchaseFlow, PurchaseStep> = {
  aftermarket: aftermarketCompleteStep,
  financing: {
    flow: "financing",
    ...consumerFinancingChecklistStep,
  },
};

export const getStepById = (flow: PurchaseFlow, stepId: number) => {
  const step = getAllPurchaseStepsForFlow(flow).find((s) => s.id === stepId);

  return step;
};

export const getStepPathname = (step: PurchaseStep, intro = false) =>
  intro ? step.introHref || step.href : step.href;

export const getFirstStep = (flow: PurchaseFlow) =>
  getAllPurchaseStepsForFlow(flow)[0];

export const getRemainingPurchaseSteps = (
  flow: PurchaseFlow,
  currentStep?: PurchaseStep
): number => {
  if (currentStep == null) {
    return 0;
  }

  const steps = getAllPurchaseStepsForFlow(flow);
  const index = steps.findIndex((s) => s.id === currentStep.id);

  if (index === -1) {
    return 0; // If the current step isn't found, return 0
  }

  return steps.length - (index + 1);
};

export const isStepAccessible = (
  currentStep: PurchaseStep,
  accessedStep: PurchaseStep,
  purchaseHasOverwriteCandidate = false
) => {
  /**
   * edge case:
   * *see this method usage on CoveragePurchaseStepAuthGuard.tsx*
   *
   * when user completed a purchase, logout, starts a new purchase and log back in,
   * at this point, they have overwrite candidate
   *
   * we want to stay at the current step and show resume modal
   * rather than redirecting them to complete step
   */

  if (currentStep.isPointOfNoReturn && !purchaseHasOverwriteCandidate) {
    return currentStep === accessedStep;
  }

  return accessedStep.order <= currentStep.order;
};

export const stepIsBeforeSigning = (step: PurchaseStep) =>
  step.flow === "aftermarket" && step.order < aftermarketContractStep.order;

export const isValidAftermarketSource = (source: string | undefined) =>
  source &&
  AftermarketSources.find(
    (s) => s.name.toLowerCase() === source.toLowerCase()
  ) !== undefined;

export const getFlowByDealType = (dealTypeId: DealTypeEnum): PurchaseFlow => {
  switch (dealTypeId) {
    case DealTypeEnum.Aftermarket:
      return "aftermarket";
    case DealTypeEnum.C2CFinancing:
      return "financing";
    default: {
      const exhaustiveCheck: never = dealTypeId;
      throw new Error(`No purchase flow found for: ${exhaustiveCheck}`);
    }
  }
};

export const showResumeForStep = (stepId: number | null) => {
  return stepId != null && stepId !== aftermarketQuoteStep.id;
};

export const showResumeQuoteStep = (stepId: number | null) => {
  return stepId != null && stepId === aftermarketQuoteStep.id;
};

export type DealPurchaseStep = {
  id: number;
  name: string;
};

export const getDealPurchaseStepsFilters = (
  allowedDealTypes?: NameId[]
): DealPurchaseStep[] => {
  const steps: DealPurchaseStep[] = [];

  allowedDealTypes?.forEach((dealType) => {
    const dealTypeId = parseInt(dealType.id, 10);
    const flow = getFlowByDealType(dealTypeId as DealTypeEnum);
    const flowSteps = getAllPurchaseStepsForFlow(flow);

    if (flow && flowSteps) {
      if (flow === "aftermarket") {
        steps.push({
          id: aftermarketQuoteStep.id,
          name: i18n?.t(aftermarketQuoteStep.nameTranslation) || "",
        });
      }
      flowSteps.forEach((flowStep) => {
        const step = allPurchaseSteps[flow].find((s) => s.id === flowStep.id);

        if (step) {
          steps.push({
            id: flowStep.id,
            name: i18n?.t(step.nameTranslation) || "",
          });
        }
      });
    }
  });

  return steps;
};
